import React from "react";
import ContactCard from "../../../components/general/contactCard";
import {
  publicLegalShellStyle,
  publicPageContainerStyle,
  publicSectionHeadingStyle,
  renderPublicLegalHeader,
} from "../../../components/general/publicPageStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const sectionHeadingStyle: React.CSSProperties = publicSectionHeadingStyle;

export default function AboutUs() {
  const authExperience = useAuthExperience();
  const facilityPlural = authExperience.vocabulary.facilityPlural;
  const facilityPluralLower = authExperience.vocabulary.facilityPluralLower;
  const websiteLabel = authExperience.vocabulary.websiteLabel;
  const operationsLabel = authExperience.vocabulary.operationsLabel;

  return (
    <div style={publicPageContainerStyle}>
      <div className="prose" style={publicLegalShellStyle}>
        {renderPublicLegalHeader(
          `About ${authExperience.brandName}`,
          <>Technology built for Washington {operationsLabel}</>,
          <>A practical software platform for {facilityPluralLower} that need secure workflows, cleaner documentation, and less operational friction.</>,
        )}

        <p>
          <strong>{authExperience.brandName.toUpperCase()}</strong> was created to solve a very specific problem: most {authExperience.vocabulary.operatorPlural} are expected to manage resident records, staff coordination, forms, compliance tasks, communication, and public visibility across disconnected tools that were never designed for the realities of small residential care settings. We built {authExperience.brandName} to bring those workflows into one secure, organized, and practical platform.
        </p>
        <p>
          Our focus is not on generic healthcare software. Our focus is Washington State {facilityPlural}: the providers who run them, the caregivers and nurses who support them, and the daily operational burden that comes with delivering consistent, compliant care in a residential setting.
        </p>

        <h3 style={sectionHeadingStyle}>Our Mission</h3>
        <p>
          Our mission is to help {authExperience.vocabulary.operatorPlural} reduce paperwork friction, improve record organization, support caregiver workflows, and make better use of technology without losing sight of the human responsibilities that define residential care.
        </p>

        <h3 style={sectionHeadingStyle}>What We Actually Build</h3>
        <p>
          {authExperience.brandName} is a software platform that combines several operational functions that {authExperience.vocabulary.facilityAbbreviationPlural} often have to manage separately. Depending on the account and subscription level, the platform may include:
        </p>
        <ul>
          <li>Resident profile management and centralized recordkeeping;</li>
          <li>Document storage, upload, organization, and export workflows;</li>
          <li>Care-plan and assessment support tools, including AI-assisted drafting workflows;</li>
          <li>Business profile and {websiteLabel} publishing tools;</li>
          <li>Caregiver and nurse profile workflows for staffing visibility and job matching;</li>
          <li>Subscription billing and account administration;</li>
          <li>Search, reference, and workflow support tools; and</li>
          <li>Cloud-hosted infrastructure designed for secure and reliable access.</li>
        </ul>

        <h3 style={sectionHeadingStyle}>Who We Serve</h3>
        <ul>
          <li>{authExperience.vocabulary.facilitySingular} owners and licensed providers;</li>
          <li>Caregivers, nurses, and authorized staff working within {authExperience.vocabulary.facilityAbbreviationPlural}; and</li>
          <li>{authExperience.vocabulary.facilityAbbreviation} operators who need a more organized way to manage documentation, staffing, and public business presence.</li>
        </ul>
        <p>
          We also support public-facing provider websites that allow potential families or interested parties to learn about a facility and submit an inquiry. That said, {authExperience.brandName} is primarily built as an operator-facing workflow and documentation platform.
        </p>

        <h3 style={sectionHeadingStyle}>Why We Started</h3>
        <p>
          {facilityPlural} often operate with limited administrative bandwidth while carrying significant obligations under HIPAA, Washington State licensing rules, record-retention expectations, and day-to-day resident care responsibilities. The burden is real: too many spreadsheets, too many folders, too many repeated forms, and too many critical tasks resting on informal processes.
        </p>
        <p>
          {authExperience.brandName} was started to reduce that fragmentation. We wanted to create a system that feels more like an operations workspace than a pile of disconnected software subscriptions.
        </p>

        <h3 style={sectionHeadingStyle}>Our Product Philosophy</h3>
        <p>
          We believe software for care settings must be practical before it is impressive. That means the product should help users find information quickly, reduce duplicate entry, support documentation accuracy, and fit the pace of real operational work. It also means being honest about what software can and cannot do.
        </p>
        <p>
          {authExperience.brandName} does not replace professional judgment. It does not provide medical advice. It does not substitute for a provider's legal, regulatory, or clinical responsibilities. Instead, it aims to support those responsibilities with better tools, clearer organization, and more reliable access to records and workflows.
        </p>

        <h3 style={sectionHeadingStyle}>Security and Trust</h3>
        <p>
          We understand that {operationsLabel} involve sensitive data, including resident health information, staff information, facility records, and business communications. For that reason, we build the platform around secure cloud infrastructure, controlled access, encrypted data handling, and role-based workflows designed to limit unnecessary exposure of sensitive information.
        </p>
        <p>
          We also understand that trust is not created by marketing language alone. It is created by being specific about what data the platform handles, where responsibility stays with the user, and how the system is intended to be used.
        </p>

        <h3 style={sectionHeadingStyle}>Washington State Focus</h3>
        <p>
          {authExperience.brandName} is designed with Washington State {operationsLabel} in mind. That focus matters. Washington providers operate within a specific regulatory environment shaped by DSHS requirements, privacy obligations, documentation expectations, and licensing standards. Our goal is to build tooling that reflects those realities rather than forcing providers into generic systems that ignore them.
        </p>

        <h3 style={sectionHeadingStyle}>How We Think About AI</h3>
        <p>
          We use AI to help reduce documentation burden, especially where structured resident information can help generate a starting draft. But we do not present AI as autonomous decision-making. Any AI-assisted output must be reviewed, edited, and approved by the responsible user before it is relied upon for care, compliance, or business use. In our view, responsible AI in this setting should accelerate work, not replace accountability.
        </p>

        <h3 style={sectionHeadingStyle}>Our Long-Term Vision</h3>
        <p>
          Our long-term vision is to become the most trusted software platform for Washington {operationsLabel} by building tools that are useful, credible, and grounded in the actual needs of providers and caregivers. That means continuing to improve workflow support, operational visibility, documentation systems, and product reliability while staying disciplined about privacy, legal exposure, and real-world usability.
        </p>

        <h3 style={sectionHeadingStyle}>Get in Touch</h3>
        <p>
          If you have questions about {authExperience.brandName}, our product direction, privacy practices, or how the platform may fit your operations, you can contact us using the information below.
        </p>
        <ContactCard />
      </div>
    </div>
  );
}
